import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useRoomsApi } from "./useRoomsApi";

const queryKeys = {
    roomPictures: (propertyId, roomId) => ['roomPictures', { propertyId, roomId }],
};

/**
 * Hook for managing pictures of a single room.
 *
 * Fetches the room's PicturesAlbum (id, displayName, order, urls) and keeps the
 * cache up-to-date after every upload or delete — upload/delete operations return
 * the updated album from the server and the result is written back to the cache
 * directly, so no extra network request is needed.
 *
 * @param {string|null} propertyId - Property identifier
 * @param {string|null} roomId     - Room identifier
 * @param {boolean}     [enabled]  - Allows callers to disable the query (default: true)
 */
export const useRoomPicturesData = (propertyId, roomId, enabled = true) => {
    const { getPictures, uploadPictures, deletePictures, reorderPictures } = useRoomsApi();

    const queryClient = useQueryClient();

    const picturesQueryData = useQuery({
        queryKey: queryKeys.roomPictures(propertyId, roomId),
        queryFn: () => getPictures(propertyId, roomId),
        staleTime: defaultStaleTime,
        enabled: enabled && !!propertyId && !!roomId,
    });

    const setInCache = (updatedAlbum) => {
        queryClient.setQueryData(queryKeys.roomPictures(propertyId, roomId), updatedAlbum);
    };

    const _uploadPictures = async (files) => {
        const updatedAlbum = await uploadPictures(propertyId, roomId, files);
        if (updatedAlbum) setInCache(updatedAlbum);
        return updatedAlbum;
    };

    const _deletePictures = async (fileNames) => {
        const updatedAlbum = await deletePictures(propertyId, roomId, fileNames);
        if (updatedAlbum) setInCache(updatedAlbum);
        return updatedAlbum;
    };

    const _reorderPictures = async (fileNamesInOrder) => {
        const updatedAlbum = await reorderPictures(propertyId, roomId, fileNamesInOrder);
        if (updatedAlbum) setInCache(updatedAlbum);
        return updatedAlbum;
    };

    return {
        picturesQueryData,
        uploadPictures: _uploadPictures,
        deletePictures: _deletePictures,
        reorderPictures: _reorderPictures,
    };
};
